import { useEffect } from "react";
import { useWebSocketContext } from "../contexts/WebSocketContext";
import { useToast } from "../components/ui/Toast/ToastProvider";
import { SystemNotificationData } from "../types/websocket";

/**
 * Custom hook that handles session start notifications from WebSocket
 * and displays them as toaster messages
 */
export const useSessionNotifications = () => {
  const { lastMessage } = useWebSocketContext();
  const { showToast } = useToast();

  useEffect(() => {
    if (!lastMessage) return;

    // Check if this is a system alert message
    if (lastMessage.type === "system_alert") {
      try {
        const data = lastMessage.data as SystemNotificationData;

        // Handle session start notifications
        if (data.notificationType === "session_start") {
          // Extract session information from additional data
          const sessionInfo = data.additionalData;
          const sessionId = sessionInfo?.sessionId || "Unknown Session";
          const dmUsername = sessionInfo?.dmUsername || "Unknown DM";
          const sessionName = sessionInfo?.sessionName || sessionId;

          // Show toaster notification
          showToast({
            type: "info",
            message: `🎲 ${data.message}`,
            duration: 8000, // Show for 8 seconds for session start notifications
          });

          console.log("✅ Session start toaster displayed:", {
            sessionId,
            dmUsername,
            sessionName,
            message: data.message,
          });
        }
        // Handle other system notifications with different styling
        else if (data.notificationType === "user_joined" || data.notificationType === "user_left") {
          // Optional: Handle join/leave notifications with shorter duration
          showToast({
            type: "info",
            message: data.message,
            duration: 3000,
          });
        }
        // Handle other system alerts
        else {
          const toastType =
            data.severity === "error" ? "error" : data.severity === "warning" ? "warning" : "info";

          showToast({
            type: toastType,
            message: data.message,
            duration: 5000,
          });
        }
      } catch (error) {
        console.error("❌ Error handling system notification:", error, lastMessage);
      }
    }
  }, [lastMessage, showToast]);

  return {
    // Could return additional functionality if needed
    isListening: true,
  };
};

export default useSessionNotifications;
